import { BasicWindow } from "./BasicWindow";
import { WheelSection, WheelSectionColors } from "../../wheel/WheelSection";
import { Global } from "../../utils/Global";
import { Effect } from "../../inventory/Effects";

export class WheelSectionDescriptor extends BasicWindow {
  private titleText!: Phaser.GameObjects.BitmapText;
  private scoreText!: Phaser.GameObjects.BitmapText;
  private sizeText!: Phaser.GameObjects.BitmapText;
  private colorLabelText!: Phaser.GameObjects.BitmapText;
  private colorValueText!: Phaser.GameObjects.BitmapText;
  private pegsText!: Phaser.GameObjects.BitmapText;
  private effectsText!: Phaser.GameObjects.BitmapText;

  // Dynamic effects display elements
  private effectElements: Phaser.GameObjects.GameObject[] = [];

  private currentSection?: WheelSection;
  private showDelay: number = 300; // ms delay before showing
  private showTimer?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene) {
    super(
      scene,
      0, // x
      0, // y
      200, // width
      150, // height
      "Button1", // textureKey
      "", // frameKey
      200, // sliceWidth
      150, // sliceHeight
      7, // leftWidth
      7, // topHeight
      7, // rightWidth
      7, // bottomHeight
    );

    this.createContent();
    this.setDepth(25000); // Above items and level report, below pause menu
  }

  private createContent(): void {
    const contentContainer = this.getContentContainer();

    // Title
    this.titleText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Section Info",
      Global.minFontSize * 1.2,
    ).setOrigin(0.5);
    contentContainer.add(this.titleText);

    // Score
    this.scoreText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Score: 0",
      Global.minFontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.scoreText);

    // Size
    this.sizeText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Size: 0°",
      Global.minFontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.sizeText);

    // Color (create a container for label + colored value)
    this.colorLabelText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Color: ",
      Global.minFontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.colorLabelText);

    this.colorValueText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Red",
      Global.minFontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.colorValueText);

    // Pegs
    this.pegsText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Pegs: 1",
      Global.minFontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.pegsText);

    // Effects
    this.effectsText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Effects: None",
      Global.minFontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.effectsText);
  }

  public showForSection(
    section: WheelSection,
    mouseX: number,
    mouseY: number,
  ): void {
    // Clear any existing timers
    this.clearTimers();

    this.currentSection = section;

    // Set up show timer
    this.showTimer = this.scene.time.delayedCall(this.showDelay, () => {
      if (this.currentSection === section) {
        this.updateContent(section);
        this.positionNearMouse(mouseX, mouseY, section);
        this.show();
      }
    });
  }

  public hideDescriptor(): void {
    // Clear any existing timers
    this.clearTimers();

    this.hide();
    this.currentSection = undefined;
  }

  public hideDescriptorImmediately(): void {
    // Clear any existing timers
    this.clearTimers();

    // Hide immediately without delay
    this.hide();
    this.currentSection = undefined;
  }

  private clearTimers(): void {
    if (this.showTimer) {
      this.showTimer.destroy();
      this.showTimer = undefined;
    }
  }

  private updateContent(section: WheelSection): void {
    const lineSpacing = Global.minFontSize * 0.3;

    // Title
    this.titleText.setText("Section Info");
    this.titleText.setFontSize(Global.minFontSize * 1.2);
    this.titleText.setOrigin(0, 0.5); // Left-aligned
    this.titleText.setPosition(0, this.titleText.displayHeight / 2);

    // Score
    this.scoreText.setText(`Score: ${section.score}`);
    this.scoreText.setFontSize(Global.minFontSize);
    this.scoreText.setOrigin(0, 0.5); // Left-aligned
    this.scoreText.setPosition(
      0,
      this.titleText.y + this.titleText.displayHeight + lineSpacing,
    );

    // Size
    this.sizeText.setText(`Size: ${Math.round(section.fieldSize)}*`);
    this.sizeText.setFontSize(Global.minFontSize);
    this.sizeText.setOrigin(0, 0.5); // Left-aligned
    this.sizeText.setPosition(
      0,
      this.scoreText.y + this.scoreText.displayHeight + lineSpacing,
    );

    // Color (position label and value side by side)
    this.colorLabelText.setText("Color: ");
    this.colorLabelText.setFontSize(Global.minFontSize);
    this.colorLabelText.setOrigin(0, 0.5); // Left-aligned
    this.colorLabelText.setPosition(
      0,
      this.sizeText.y + this.sizeText.displayHeight + lineSpacing,
    );

    this.colorValueText.setText(this.getColorName(section.fieldColor));
    this.colorValueText.setFontSize(Global.minFontSize);
    this.colorValueText.setOrigin(0, 0.5); // Left-aligned
    this.colorValueText.setTint(section.fieldColor);
    this.colorValueText.setPosition(
      this.colorLabelText.displayWidth,
      this.colorLabelText.y,
    );

    // Pegs
    this.pegsText.setText(`Pegs: ${section.pegs}`);
    this.pegsText.setFontSize(Global.minFontSize);
    this.pegsText.setOrigin(0, 0.5); // Left-aligned
    this.pegsText.setPosition(
      0,
      this.colorValueText.y + this.colorValueText.displayHeight + lineSpacing,
    );

    // Clear any existing effect elements
    this.clearEffectElements();

    // Effects
    let currentY = this.pegsText.y + this.pegsText.displayHeight + lineSpacing;

    if (section.effects.length === 0) {
      this.effectsText.setText("Effects: None");
      this.effectsText.setFontSize(Global.minFontSize);
      this.effectsText.setOrigin(0, 0.5); // Left-aligned
      this.effectsText.setPosition(0, currentY);
    } else {
      this.effectsText.setText("Effects:");
      this.effectsText.setFontSize(Global.minFontSize);
      this.effectsText.setOrigin(0, 0.5); // Left-aligned
      this.effectsText.setPosition(0, currentY);
      currentY += this.effectsText.displayHeight + lineSpacing;

      // Create bullet points for each effect
      section.effects.forEach((effect) => {
        currentY = this.createEffectBulletPoint(effect, currentY, lineSpacing);
      });
    }

    // Use parent's resize method
    this.resizeToFitContent();
  }

  private clearEffectElements(): void {
    // Remove all dynamic effect elements from content container
    this.effectElements.forEach((element) => {
      this.getContentContainer().remove(element);
      element.destroy();
    });
    this.effectElements = [];
  }

  private createEffectBulletPoint(
    effect: Effect,
    startY: number,
    lineSpacing: number,
  ): number {
    const indent = Global.minFontSize / 2; // Indent for bullet points

    // Create effect icon from items atlas
    const icon = new Phaser.GameObjects.Image(
      this.scene,
      indent,
      startY,
      "items",
      effect.icon,
    ).setOrigin(0, 0.5); // Left-aligned
    icon.setDisplaySize(Global.minFontSize * 1.2, Global.minFontSize * 1.2);
    this.getContentContainer().add(icon);
    this.effectElements.push(icon);

    // Create effect name
    const effectName = effect.name;
    const nameText = new Phaser.GameObjects.BitmapText(
      this.scene,
      icon.x + icon.displayWidth + Global.minFontSize * 0.1,
      startY,
      "fnt_paintBasic",
      effectName,
      Global.minFontSize,
    ).setOrigin(0, 0.5); // Left-aligned
    this.getContentContainer().add(nameText);
    this.effectElements.push(nameText);

    let descriptionY = nameText.y + nameText.displayHeight + lineSpacing;
    // Create duration text if this effect has a duration
    if (effect.duration > 0) {
      const durationDisplay = new Phaser.GameObjects.BitmapText(
        this.scene,
        indent,
        nameText.y + nameText.displayHeight + lineSpacing,
        "fnt_paintBasic",
        `Triggers left: ${effect.duration.toString()}`,
        Global.minFontSize, // Slightly smaller than name
      ).setOrigin(0, 0.5); // Left-aligned
      durationDisplay.setTint(0x908cc7  );
      this.getContentContainer().add(durationDisplay);
      this.effectElements.push(durationDisplay);
      descriptionY =
        durationDisplay.y + durationDisplay.displayHeight + lineSpacing;
    }

    const descText = new Phaser.GameObjects.BitmapText(
      this.scene,
      indent,
      descriptionY,
      "fnt_paintBasic",
      effect.description,
      Global.minFontSize, // Slightly smaller for description
    ).setOrigin(0, 0.5); // Left-aligned
    descText.setTint(0x604c7d);
    this.getContentContainer().add(descText);
    this.effectElements.push(descText);

    return descriptionY + descText.displayHeight + lineSpacing * 2;
  }

  private getColorName(color: WheelSectionColors): string {
    switch (color) {
      case WheelSectionColors.RED:
        return "Red";
      case WheelSectionColors.GREEN:
        return "Green";
      case WheelSectionColors.BLUE:
        return "Blue";
      case WheelSectionColors.YELLOW:
        return "Yellow";
      case WheelSectionColors.BLACK:
        return "Black";
      default:
        return "Unknown";
    }
  }

  private positionNearMouse(
    mouseX: number,
    mouseY: number,
    section: WheelSection,
  ): void {
    const padding = Global.minFontSize;
    const windowBounds = this.getBounds();
    const windowWidth = windowBounds.width;
    const windowHeight = windowBounds.height;
    const screenWidth = this.scene.scale.width;
    const screenHeight = this.scene.scale.height;

    // Get wheel position (wheel is the parent of the section)
    const wheel = section.wheel;
    const wheelWorldPos = wheel.getWorldPoint();
    const wheelCenterX = wheelWorldPos.x;

    // Determine preferred side based on mouse position relative to wheel center
    let preferRight = true;
    if (mouseX < wheelCenterX) {
      // Mouse is on left side of wheel, prefer showing descriptor on the right
      preferRight = false;
    } else {
      // Mouse is on right side of wheel, prefer showing descriptor on the left
      preferRight = true;
    }

    // Start with preferred horizontal position
    let x: number;
    if (preferRight) {
      x = mouseX + windowWidth / 2 + padding;
    } else {
      x = mouseX - windowWidth / 2 - padding;
    }

    // Check if preferred position fits, if not use opposite side
    if (preferRight && x + windowWidth / 2 > screenWidth) {
      // Preferred right doesn't fit, try left
      x = mouseX - windowWidth / 2 - padding;
    } else if (!preferRight && x - windowWidth / 2 < 0) {
      // Preferred left doesn't fit, try right
      x = mouseX + windowWidth / 2 + padding;
    }

    // Vertical positioning (centered on mouse)
    let y = mouseY;

    // Check vertical positioning
    if (y + windowHeight / 2 > screenHeight) {
      // Window would go off the bottom edge, position above mouse
      y = mouseY - windowHeight / 2 - padding;
    } else if (y - windowHeight / 2 < 0) {
      // Window would go off the top edge, position below mouse
      y = mouseY + windowHeight / 2 + padding;
    }

    // Final boundary checks to ensure window stays fully on screen
    x = Math.max(windowWidth / 2, Math.min(screenWidth - windowWidth / 2, x));
    y = Math.max(
      windowHeight / 2,
      Math.min(screenHeight - windowHeight / 2, y),
    );

    this.setPosition(x, y);
  }

  public override destroy(): void {
    this.clearTimers();
    this.clearEffectElements();
    this.scene.tweens.killTweensOf(this);
    super.destroy();
  }
}
